import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { SuccessToast, useSuccessToast } from '../../shared/SuccessToast';
import { buildContentVideoUrl } from '../../../config';
import loadingStyles from '../../shared/Loading.module.css';
import {
  checkUserContentAction,
  fetchUserContents,
  uncheckUserContentAction,
  watchUserContent,
} from '../../../utils/api';
import { getFirstNameFromStorage } from '../../../utils/helper';
import { triggerContentReflection } from '../../../utils/contentReflectionBridge';
import VideoCover from '../your-quick-richuels/VideoCover';
import playerMountains from '../../../assets/progress-player-valley-mountains.png';
import { TargetArrowIcon } from '../your-quick-richuels/icons';
import {
  ArrowIcon,
  ChartIcon,
  CheckBadgeIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  HeadphonesIcon,
  PauseIcon,
  PeopleIcon,
  PlayIcon,
  SkipBackIcon,
  SkipForwardIcon,
  SpeakerIcon,
  SpeakerMutedIcon,
} from './icons';
import {
  YOUR_PROGRESS_PATH,
  actionEntries,
  contentProgressValue,
  formatClock,
  markPlaybackStarted,
  summarizeContents,
} from './progressData';
import styles from './ProgressSessionPage.module.css';

const SPEEDS = [1, 1.25, 1.5];
const SKIP_SECONDS = 15;
const SEEK_KEY_SECONDS = 5;

function seekRatioFromPointer(event) {
  const bounds = event.currentTarget.getBoundingClientRect();
  return bounds.width ? (event.clientX - bounds.left) / bounds.width : 0;
}

function SessionStat({ icon, eyebrow, value, hint, tone }) {
  return (
    <article className={styles.statCard}>
      <span className={`${styles.statIcon} ${styles[tone] || ''}`} aria-hidden="true">
        {icon}
      </span>
      <div className={styles.statCopy}>
        <p className={styles.statEyebrow}>{eyebrow}</p>
        <p className={styles.statValue}>{value}</p>
        <p className={styles.statHint}>{hint}</p>
      </div>
    </article>
  );
}

function barTone(progress, status) {
  if (status === 'completed' || progress >= 100) return 'done';
  if (progress > 0) return 'active';
  return 'idle';
}

function ProgressMeter({ progress, tone, labelId }) {
  const value = Math.min(100, Math.max(0, progress));
  return (
    <span
      className={styles.meter}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
      aria-labelledby={labelId}
    >
      <span className={styles.meterTrack} aria-hidden="true">
        <span
          className={`${styles.meterFill} ${styles[`meter-${tone}`] || ''}`}
          style={{ width: `${value}%` }}
        />
      </span>
      <span className={styles.meterValue}>{value}%</span>
    </span>
  );
}

function MobileBackBar({ onBack }) {
  return (
    <div className={styles.mobileBackBar}>
      <button type="button" className={styles.mobileBackButton} onClick={onBack}>
        <span className={styles.mobileBackIcon} aria-hidden="true">
          <ChevronRightIcon className={styles.mobileBackChevron} />
        </span>
        <span className={styles.mobileBackLabel}>Back</span>
      </button>
    </div>
  );
}

function SessionLoading({ onBack }) {
  return (
    <div className={styles.page} data-page="your-progress">
      <MobileBackBar onBack={onBack} />
      <div className={styles.mainContent} aria-busy="true" aria-live="polite">
        <p className={styles.srOnly}>Loading this Quick Richuel…</p>
        <div className={styles.statsGrid} aria-hidden="true">
          {Array.from({ length: 3 }, (_, index) => (
            <article key={index} className={styles.statCard}>
              <span className={`${styles.skeletonBlock} ${styles.skeletonIcon}`} />
              <div className={styles.statCopy}>
                <span className={`${styles.skeletonBlock} ${styles.skeletonEyebrow}`} />
                <span className={`${styles.skeletonBlock} ${styles.skeletonValue}`} />
                <span className={`${styles.skeletonBlock} ${styles.skeletonHint}`} />
              </div>
            </article>
          ))}
        </div>

        <div className={styles.sessionGrid} aria-hidden="true">
          <section className={`${styles.playerCard} ${styles.skeletonPlayer}`}>
            <div className={styles.skeletonPlayerBody}>
              <div className={loadingStyles['loading-spinner']}>
                <div className={loadingStyles['spinner-ring']} />
                <div className={loadingStyles['spinner-ring']} />
                <div className={loadingStyles['spinner-ring']} />
              </div>
              <p className={styles.skeletonLabel}>Loading this Quick Richuel…</p>
            </div>
          </section>
          <aside className={styles.sidePanel}>
            <div className={styles.skeletonPanel}>
              <span className={`${styles.skeletonBlock} ${styles.skeletonPanelLine}`} />
              <span className={`${styles.skeletonBlock} ${styles.skeletonPanelLineShort}`} />
            </div>
            <section className={styles.actionsCard} aria-hidden="true">
              <span className={`${styles.skeletonBlock} ${styles.skeletonActionsTitle}`} />
              {Array.from({ length: 3 }, (_, index) => (
                <span key={index} className={`${styles.skeletonBlock} ${styles.skeletonActionRow}`} />
              ))}
            </section>
            <div className={styles.progressCard} aria-hidden="true">
              <span className={`${styles.skeletonBlock} ${styles.skeletonProgressLabel}`} />
              <span className={`${styles.skeletonBlock} ${styles.skeletonMeter}`} />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function reflectionsFromContents(contents) {
  return (Array.isArray(contents) ? contents : []).flatMap((item) => {
    if (!item?.reflected_on || item.confidence == null || item.anxiety == null) return [];
    return [{
      content_id: item.id,
      title: item.title,
      date: String(item.reflected_on).slice(0, 10),
      confidence: item.confidence,
      anxiety: item.anxiety,
      completed: true,
    }];
  });
}

function actionRows(content) {
  const { checked } = contentProgressValue(content);
  return actionEntries(content).map((item) => ({
    id: `${content?.id || 'content'}-${item.index}`,
    index: item.index,
    title: item.title,
    status: checked.has(item.index) ? 'completed' : 'not_started',
  }));
}

function ProgressSessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const firstName = getFirstNameFromStorage() || 'there';
  const { message: toastMessage, isExiting: toastExiting, showToast } = useSuccessToast();

  const videoRef = useRef(null);
  const [content, setContent] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [videoError, setVideoError] = useState('');
  const [savingAction, setSavingAction] = useState(false);
  const [playbackStarted, setPlaybackStarted] = useState(false);
  const [coverFailed, setCoverFailed] = useState(false);
  const [frameFailed, setFrameFailed] = useState(false);

  const applyContent = useCallback((nextContent, contents) => {
    setContent(nextContent);
    if (Array.isArray(contents)) {
      setCatalog(contents);
    } else if (nextContent?.id) {
      setCatalog((prev) => prev.map((item) => (
        String(item?.id) === String(nextContent.id) ? nextContent : item
      )));
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    const { signal } = ac;
    const run = async () => {
      setLoading(true);
      setMissing(false);
      setLoadError(false);
      setVideoError('');
      setIsPlaying(false);
      setPlaybackStarted(false);
      setCoverFailed(false);
      setFrameFailed(false);
      setCurrentTime(0);
      setDuration(0);
      try {
        const result = await fetchUserContents(navigate, { signal });
        if (signal.aborted) return;
        const match = (result?.contents || []).find((item) => String(item?.id) === String(sessionId));
        if (!result) {
          setLoadError(true);
          setContent(null);
          return;
        }
        if (!match) {
          setMissing(true);
          setContent(null);
          return;
        }
        applyContent(match, result.contents);
      } catch (error) {
        if (error?.name === 'AbortError' || signal.aborted) return;
        setLoadError(true);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };
    run();
    return () => ac.abort();
  }, [applyContent, navigate, sessionId]);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const total = Number.isFinite(video.duration) ? video.duration : 0;
    setDuration(total);
    setCurrentTime(video.currentTime || 0);
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentTime(video.currentTime || 0);
  }, []);

  const handleBack = useCallback(() => {
    const historyIndex = window.history.state?.idx;
    if (typeof historyIndex === 'number' && historyIndex > 0) {
      navigate(-1);
      return;
    }
    navigate(YOUR_PROGRESS_PATH);
  }, [navigate]);

  const openReflectionIfCompleted = useCallback((previous, next, contents) => {
    if (!next?.id) return false;
    const wasDone = previous ? contentProgressValue(previous).status === 'completed' : false;
    const nowDone = contentProgressValue(next).status === 'completed';
    if (!nowDone || wasDone || next.reflected_on) return false;
    const merged = (Array.isArray(contents) ? contents : []).map((item) => (
      String(item?.id) === String(next.id) ? next : item
    ));
    if (!merged.some((item) => String(item?.id) === String(next.id))) merged.push(next);
    return triggerContentReflection({
      contentId: next.id,
      contentTitle: next.title,
      reflections: reflectionsFromContents(merged),
      onReflected: (updated) => {
        if (updated) applyContent(updated);
      },
      onDismiss: handleBack,
    });
  }, [applyContent, handleBack]);

  const markWatched = useCallback(async () => {
    setIsPlaying(false);
    if (!content?.id || content.video_watched) {
      setShowCelebration(true);
      return;
    }
    const result = await watchUserContent(navigate, content.id);
    if (result?.ok && result.content) {
      const opened = openReflectionIfCompleted(content, result.content, catalog);
      applyContent(result.content);
      if (!opened) setShowCelebration(true);
      return;
    }
    if (result && result.ok === false) showToast(result.message || 'Could not save listen progress.');
  }, [applyContent, catalog, content, navigate, openReflectionIfCompleted, showToast]);

  const togglePlay = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
      return;
    }
    try {
      await video.play();
      setIsPlaying(true);
      setVideoError('');
    } catch {
      setVideoError('Listening is not available yet.');
    }
  }, [isPlaying]);

  const skip = useCallback((delta) => {
    const video = videoRef.current;
    if (!video) return;
    const total = duration || video.duration || 0;
    video.currentTime = Math.min(Math.max(0, (video.currentTime || 0) + delta), total);
    setCurrentTime(video.currentTime);
  }, [duration]);

  const seekToRatio = useCallback((ratio) => {
    const video = videoRef.current;
    const total = duration || video?.duration || 0;
    if (!video || !Number.isFinite(total) || total <= 0) return;
    const next = Math.min(1, Math.max(0, ratio)) * total;
    video.currentTime = next;
    setCurrentTime(next);
  }, [duration]);

  const handleSeekPointerDown = useCallback((event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsScrubbing(true);
    seekToRatio(seekRatioFromPointer(event));
  }, [seekToRatio]);

  const handleSeekPointerMove = useCallback((event) => {
    if (!event.currentTarget.hasPointerCapture?.(event.pointerId)) return;
    seekToRatio(seekRatioFromPointer(event));
  }, [seekToRatio]);

  const handleSeekPointerEnd = useCallback((event) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setIsScrubbing(false);
  }, []);

  const handleSeekKeyDown = useCallback((event) => {
    const steps = {
      ArrowRight: SEEK_KEY_SECONDS,
      ArrowUp: SEEK_KEY_SECONDS,
      ArrowLeft: -SEEK_KEY_SECONDS,
      ArrowDown: -SEEK_KEY_SECONDS,
    };
    if (event.key in steps) {
      event.preventDefault();
      skip(steps[event.key]);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      seekToRatio(event.key === 'Home' ? 0 : 1);
    }
  }, [seekToRatio, skip]);

  const changeSpeed = useCallback((rate) => {
    setPlaybackRate(rate);
    if (videoRef.current) videoRef.current.playbackRate = rate;
  }, []);

  const changeVolume = useCallback((event) => {
    const next = Number(event.target.value);
    setVolume(next);
    setMuted(next === 0);
  }, []);

  const toggleMute = useCallback(() => {
    if (muted && volume === 0) setVolume(0.5);
    setMuted((prev) => !prev);
  }, [muted, volume]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = volume;
    video.muted = muted;
  }, [volume, muted, content]);

  useEffect(() => {
    const onKey = (event) => {
      if (
        event.target instanceof Element
        && event.target.closest('input, textarea, select, button, [contenteditable="true"]')
      ) return;
      if (event.code === 'Space') {
        event.preventDefault();
        togglePlay();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay]);

  const summary = useMemo(() => summarizeContents(catalog), [catalog]);
  const actions = useMemo(() => (content ? actionRows(content) : []), [content]);
  const videoDone = Boolean(content?.video_watched);
  const sessionProgress = content ? contentProgressValue(content) : { progress: 0, status: 'not_started' };
  const contentDone = sessionProgress.status === 'completed';
  const videoUrl = buildContentVideoUrl({ filename: content?.video });
  const coverUrl = buildContentVideoUrl({ filename: content?.cover_image });
  const showCoverImage = Boolean(videoUrl) && Boolean(coverUrl) && !coverFailed && !playbackStarted;
  const showVideoFrame = Boolean(videoUrl) && !showCoverImage && !frameFailed && !playbackStarted;
  const durationLabel = duration > 0 ? formatClock(duration) : 'Listen';
  const progressPct = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;
  const completedActionCount = useMemo(
    () => actions.filter((item) => item.status === 'completed').length,
    [actions],
  );

  const toggleAction = useCallback(async (item) => {
    if (!content?.id || savingAction) return;
    setSavingAction(true);
    const request = item.status === 'completed' ? uncheckUserContentAction : checkUserContentAction;
    const result = await request(navigate, content.id, item.index);
    setSavingAction(false);
    if (result?.ok && result.content) {
      const opened = openReflectionIfCompleted(content, result.content, catalog);
      applyContent(result.content);
      if (!opened) {
        showToast(item.status === 'completed' ? 'Action unchecked.' : 'Action checked.');
      }
      return;
    }
    showToast(result?.message || 'Could not update this action.');
  }, [applyContent, catalog, content, navigate, openReflectionIfCompleted, savingAction, showToast]);

  if (loading) return <SessionLoading onBack={handleBack} />;

  if (loadError) {
    return (
      <div className={styles.page} data-page="your-progress">
        <MobileBackBar onBack={handleBack} />
        <div className={styles.mainContent}>
          <div className={styles.statusShell} role="alert">
            <p className={styles.statusMessage}>We couldn&apos;t load this Quick Richuel. Go back and try again.</p>
          </div>
        </div>
      </div>
    );
  }

  if (missing || !content) {
    return <Navigate to={YOUR_PROGRESS_PATH} replace />;
  }

  const volumeLevel = muted ? 0 : volume;
  const canSeek = duration > 0;
  const description = String(content.description || '').trim();

  const statusBadge = contentDone || videoDone ? (
    <span className={styles.completedBadge}>
      <CheckIcon />
      {contentDone ? 'Completed' : 'Listened'}
    </span>
  ) : (
    <span className={styles.liveBadge}>
      <span className={styles.liveDot} aria-hidden="true" />
      In progress
    </span>
  );

  return (
    <div className={styles.page} data-page="your-progress">
      <SuccessToast message={toastMessage} isExiting={toastExiting} />
      <MobileBackBar onBack={handleBack} />

      <div className={styles.mainContent}>
        <section className={styles.statsGrid} aria-label="Session stats">
          <SessionStat
            icon={<HeadphonesIcon />}
            eyebrow="Listened"
            value={summary.watched}
            hint="Finished listening"
            tone="statIconBlue"
          />
          <SessionStat
            icon={<CheckBadgeIcon />}
            eyebrow="Actions done"
            value={summary.actionsCompleted}
            hint="Action items completed"
            tone="statIconTeal"
          />
          <SessionStat
            icon={<ChartIcon />}
            eyebrow="Completed"
            value={summary.completed}
            hint="Quick Richuels finished"
            tone="statIconGold"
          />
        </section>

        <div className={styles.sessionGrid}>
          <section
            className={[
              styles.playerCard,
              videoUrl ? '' : styles.playerCardArt,
              isPlaying ? styles.isPlaying : '',
            ].filter(Boolean).join(' ')}
            aria-label={`${content.title} listen`}
          >
            {videoUrl ? (
              <div className={styles.playerMedia}>
                <video
                  ref={videoRef}
                  className={[styles.playerVideo, (showCoverImage || showVideoFrame) ? styles.playerVideoIdle : ''].filter(Boolean).join(' ')}
                  src={videoUrl}
                  poster={coverUrl || undefined}
                  playsInline
                  preload="metadata"
                  onClick={togglePlay}
                  onLoadedMetadata={handleLoadedMetadata}
                  onTimeUpdate={handleTimeUpdate}
                  onEnded={markWatched}
                  onPlay={() => {
                    setIsPlaying(true);
                    setPlaybackStarted(true);
                    markPlaybackStarted(content?.id);
                  }}
                  onPause={() => setIsPlaying(false)}
                  onError={() => setVideoError('Could not start listening.')}
                />
                {showCoverImage || showVideoFrame ? (
                  <div className={styles.mediaCover}>
                    {showCoverImage ? (
                      <img
                        src={coverUrl}
                        alt=""
                        className={styles.mediaCoverImg}
                        decoding="async"
                        onError={() => setCoverFailed(true)}
                      />
                    ) : (
                      <VideoCover
                        src={videoUrl}
                        className={styles.mediaCoverImg}
                        onFail={() => setFrameFailed(true)}
                      />
                    )}
                  </div>
                ) : null}
                <span className={styles.mediaPlayHint} aria-hidden="true">
                  <PlayIcon />
                </span>
                <div className={styles.mediaTop}>{statusBadge}</div>
              </div>
            ) : (
              <>
                <img
                  src={playerMountains}
                  alt=""
                  className={styles.playerArt}
                  decoding="async"
                />
                <div className={styles.playerShade} />
              </>
            )}

            <div className={styles.playerBody}>
              {videoUrl ? null : <div className={styles.playerTop}>{statusBadge}</div>}

              <div className={styles.playerCopy}>
                <h1 className={styles.playerTitle}>{content.title}</h1>
                <p className={styles.playerMeta}>
                  <span><ClockIcon /> {durationLabel}</span>
                  <span className={styles.metaDot} aria-hidden="true" />
                  <span><PeopleIcon /> {content.category || 'Richuel'}</span>
                </p>
              </div>

              <div className={styles.playerControls}>
                <div className={styles.seekWrap}>
                  <div
                    className={`${styles.seekBar} ${isScrubbing ? styles.seekBarActive : ''}`}
                    style={{ '--seek-pct': `${progressPct}%` }}
                    role="slider"
                    tabIndex={canSeek ? 0 : -1}
                    aria-label="Seek"
                    aria-valuemin={0}
                    aria-valuemax={Math.round(duration)}
                    aria-valuenow={Math.round(currentTime)}
                    aria-valuetext={`${formatClock(currentTime)} of ${formatClock(duration)}`}
                    aria-disabled={!canSeek}
                    onPointerDown={handleSeekPointerDown}
                    onPointerMove={handleSeekPointerMove}
                    onPointerUp={handleSeekPointerEnd}
                    onPointerCancel={handleSeekPointerEnd}
                    onLostPointerCapture={() => setIsScrubbing(false)}
                    onKeyDown={handleSeekKeyDown}
                  >
                    <span className={styles.seekTrack}>
                      <span className={styles.seekFill} />
                    </span>
                    <span className={styles.seekThumb} />
                  </div>
                  <div className={styles.seekTimes}>
                    <span>{formatClock(currentTime)}</span>
                    <span>{formatClock(duration)}</span>
                  </div>
                </div>

                {videoError ? <p className={styles.audioHint}>{videoError}</p> : null}

                <div className={styles.controlRow}>
                  <div className={styles.volumeControl}>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={toggleMute}
                      aria-label={volumeLevel === 0 ? 'Unmute' : 'Mute'}
                    >
                      {volumeLevel === 0 ? <SpeakerMutedIcon /> : <SpeakerIcon />}
                    </button>
                    <input
                      type="range"
                      className={styles.volumeRange}
                      min="0"
                      max="1"
                      step="0.01"
                      value={volumeLevel}
                      onChange={changeVolume}
                      style={{ '--fill': `${volumeLevel * 100}%` }}
                      aria-label="Volume"
                    />
                  </div>

                  <div className={styles.controlCenter}>
                    <button
                      type="button"
                      className={styles.skipBtn}
                      onClick={() => skip(-SKIP_SECONDS)}
                      aria-label={`Back ${SKIP_SECONDS} seconds`}
                    >
                      <SkipBackIcon seconds={SKIP_SECONDS} />
                    </button>
                    <button
                      type="button"
                      className={styles.playBtn}
                      onClick={togglePlay}
                      aria-label={isPlaying ? 'Pause' : 'Play'}
                    >
                      {isPlaying ? <PauseIcon /> : <PlayIcon />}
                    </button>
                    <button
                      type="button"
                      className={styles.skipBtn}
                      onClick={() => skip(SKIP_SECONDS)}
                      aria-label={`Forward ${SKIP_SECONDS} seconds`}
                    >
                      <SkipForwardIcon seconds={SKIP_SECONDS} />
                    </button>
                  </div>

                  <div className={styles.speedGroup} role="group" aria-label="Playback speed">
                    {SPEEDS.map((rate) => (
                      <button
                        key={rate}
                        type="button"
                        className={`${styles.speedBtn} ${playbackRate === rate ? styles.speedBtnActive : ''}`}
                        onClick={() => changeSpeed(rate)}
                        aria-pressed={playbackRate === rate}
                      >
                        {rate}x
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <aside className={styles.sidePanel}>
            {showCelebration ? (
              <div className={styles.celebration}>
                <button
                  type="button"
                  className={styles.dismissBtn}
                  onClick={() => setShowCelebration(false)}
                >
                  <ChevronDownIcon />
                  Drag Down to Dismiss
                </button>
                <div className={styles.celebrationBadge} aria-hidden="true">
                  <span className={`${styles.spark} ${styles.s1}`} />
                  <span className={`${styles.spark} ${styles.s2}`} />
                  <span className={`${styles.spark} ${styles.s3}`} />
                  <span className={`${styles.spark} ${styles.s4}`} />
                  <span className={`${styles.spark} ${styles.s5}`} />
                  <CheckIcon />
                </div>
                <h2 className={styles.celebrationTitle}>Richuel Listened!</h2>
                <p className={styles.celebrationHello}>Great job, {firstName}!</p>
                <p className={styles.celebrationCopy}>You&apos;ve finished listening. Now put it into action.</p>
              </div>
            ) : null}

            <div className={styles.sideIntro}>
              <h2 className={styles.sideTitle}>{content.title}</h2>
              {description ? <p className={styles.sideDescription}>{description}</p> : null}
            </div>

            <section className={styles.actionsCard} aria-labelledby="actions-title">
              <div className={styles.actionsHeader}>
                <div className={styles.actionsHeading}>
                  <p className={styles.actionsEyebrow}>Action</p>
                  <h2 id="actions-title">Actions for this Richuel</h2>
                </div>
                <p className={styles.actionCount} aria-live="polite">
                  <span className={styles.actionCountValue}>{completedActionCount}</span>
                  <span className={styles.actionCountTotal}>/ {actions.length}</span>
                </p>
              </div>
              <div className={styles.actionList}>
                {actions.map((item) => {
                  const done = item.status === 'completed';
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`${styles.actionItem} ${done ? styles.actionItemDone : ''}`}
                      aria-pressed={done}
                      onClick={() => toggleAction(item)}
                      disabled={savingAction}
                    >
                      <span className={`${styles.actionRadio} ${done ? styles.actionRadioDone : ''}`} aria-hidden="true">
                        {done ? <CheckIcon /> : null}
                      </span>
                      <span className={styles.actionCopy}>
                        <p className={styles.actionTitle}>{item.title}</p>
                        <span className={`${styles.actionStatus} ${done ? styles.actionStatusDone : ''}`}>
                          {done ? 'Done' : 'To do'}
                        </span>
                      </span>
                    </button>
                  );
                })}
                {actions.length === 0 ? (
                  <p className={styles.emptyActions}>No actions for this Richuel.</p>
                ) : null}
              </div>
            </section>

            <section className={styles.progressCard} aria-labelledby="session-progress-label">
              <p id="session-progress-label" className={styles.progressLabel}>Progress</p>
              <ProgressMeter
                progress={sessionProgress.progress}
                tone={barTone(sessionProgress.progress, sessionProgress.status)}
                labelId="session-progress-label"
              />
            </section>

          </aside>
        </div>

        <section className={styles.tipBanner} aria-label="Action tracking tip">
          <div className={styles.tipCopy}>
            <span className={styles.tipIconWrap} aria-hidden="true">
              <TargetArrowIcon className={styles.tipIcon} />
            </span>
            <div className={styles.tipText}>
              <h2 className={styles.tipTitle}>Tip:</h2>
              <p className={styles.tipSubtitle}>
                Mark actions complete as you go. Your progress builds over time.
              </p>
            </div>
          </div>
          <button
            type="button"
            className={styles.tipCta}
            onClick={() => showToast('Finish listening, then check or uncheck each action. Progress is saved to your account.')}
          >
            How action tracking works
            <ArrowIcon className={styles.tipCtaArrow} />
          </button>
        </section>
      </div>
    </div>
  );
}

export default ProgressSessionPage;
